import {
  Box,
  Button,
  IconButton,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, RefObject } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialog, useOrder } from '@/logic/state';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { DateTime } from '@/utility/luxon';
import { ILocale } from '@/lang/types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MyTransactions from '../transactions/my-transactions';
import { Transaction } from '../../services/types';

import { checkIsRtl } from '@/lang/utils';
import isEmpty from 'lodash/isEmpty';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useGetSettings } from '@/logic/services';
import { useInitTopUp } from '../../services/_api';
import { useRouter } from 'next/router';
import { useUserBalance } from '../../hooks/use-user-balance';
import { useWalletStore } from '../../store/wallet-store';
import { isTopUpWalletEnabled } from '@/constants/wallet';

import ImageBoxFuture from '@/components/shared/image-box-future';

const WalletExpiryDateIcon = '/icons/profile/wallet-expiry-date.svg';

interface WalletInfoProps {
  myWalletRef: RefObject<HTMLDivElement>;
  accordionSx: SxProps;
  accordionBoxIconSx: SxProps;
  transactions: Transaction[];
  isFetchingTransactions: boolean;
  isErrorTransactions: boolean;
  refetchTransactions: () => void;
}

export const WalletInfo: FC<WalletInfoProps> = ({
  myWalletRef,
  accordionSx,
  accordionBoxIconSx,
  transactions,
  isFetchingTransactions,
  isErrorTransactions,
  refetchTransactions,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();

  const setWallet = useWalletStore(s => s.setWallet, shallow);
  const setDialog = useDialog(s => s.setDialog, shallow);
  const [setStcPayData] = useOrder(s => [s.setStcPayData], shallow);
  const isRtl = checkIsRtl(locale as ILocale);

  const { userFormattedWalletBalance, userNextExpiredWalletTransaction } =
    useUserBalance();

  const { isFetching: isFetchingSettings, refetch: refetchSettings } = useGetSettings(
    {},
    {
      enabled: false,
    }
  );

  const initTopUp = useInitTopUp({});

  const isLoading = isFetchingSettings || initTopUp.isLoading;

  const openAddTopUpDialog = async () => {
    setStcPayData(null);
    try {
      const settingsPromise = refetchSettings();
      const initTopUpPromise = initTopUp.safeMutateAsync({});

      const [settingsData, initTopUpData] = await Promise.all([
        settingsPromise,
        initTopUpPromise,
      ]);

      if (!isEmpty(settingsData.data) && !isEmpty(initTopUpData)) {
        setWallet(prevState => ({
          temporaryAddedCreditCards: [],
          creditCardsCvvList: [],
          topUp: {
            ...initTopUpData.topUp,
            amount: prevState?.topUp?.amount,
          },
          minAmountTopUp: settingsData.data.minAmountTopUp,
          maxAmountTopUp: settingsData.data.maxAmountTopUp,
        }));
        setDialog({
          wrapper: true,
          isAddTopUpDialog: true,
        });
      } else {
        setWallet(prevState => ({
          topUp: {
            amount: prevState?.topUp?.amount,
          },
        }));
      }
    } catch (error) {}
  };

  return (
    <>
      <Box
        ref={myWalletRef}
        sx={{
          background: theme.palette.common.white,
          boxShadow: theme.customShadows.cardShadow,
          borderRadius: '16px',
          p: { tablet: pxToRem(32), mobile: '22px' },
          pl: { tablet: pxToRem(20), mobile: '17px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography
              sx={{
                color: theme.palette.grey[50],
                fontSize: pxToRem(16),
                fontWeight: '400',
                lineHeight: '150%',
              }}
            >
              <FormattedMessage id='wallet.my.balance' />
            </Typography>
            <Typography
              sx={{
                color: theme.palette.grey[80],
                fontSize: pxToRem(22),
                fontWeight: '700',
                lineHeight: '150%',
              }}
            >
              {userFormattedWalletBalance}&nbsp;
              <FormattedMessage id='currency.SAR' />
            </Typography>
          </Box>
          {isTopUpWalletEnabled && (
            <Button
              id={'wallet-top-up'}
              variant='secondary'
              loading={isLoading}
              startIcon={
                <AddIcon
                  sx={{
                    width: '16px',
                    height: '16px',
                  }}
                />
              }
              sx={{
                '& .MuiButton-startIcon': {
                  marginRight: '4px',
                },
                // v7: LoadingButton renders the core Button, which applies
                // `.MuiButton-loading` (the old `.MuiLoadingButton-loading` matches nothing)
                '&.MuiButton-loading': {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
              onClick={openAddTopUpDialog}
            >
              <Typography sx={{ fontSize: pxToRem(16), fontWeight: '700' }}>
                <FormattedMessage id='wallet.top.up' />
              </Typography>
            </Button>
          )}
        </Box>
        {userNextExpiredWalletTransaction && (
          <Button
            sx={{
              marginTop: '16px',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            onClick={() =>
              setDialog({
                wrapper: true,
                isWalletExpiryDetailsDialog: true,
              })
            }
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                component='div'
                sx={{
                  ...accordionBoxIconSx,
                  backgroundColor: theme.palette.primary.main,
                  width: pxToRem(46),
                  height: pxToRem(46),
                }}
              >
                <ImageBoxFuture
                  img={WalletExpiryDateIcon}
                  title={intl.formatMessage({
                    id: 'alt.my_wallet.expiry.date',
                  })}
                  objectFit='contain'
                  sx={{
                    width: { tablet: pxToRem(33), mobile: '40px' },
                    height: { tablet: pxToRem(33), mobile: '24px' },
                  }}
                />
              </Box>

              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography
                  component='span'
                  sx={{
                    fontSize: pxToRem(16),
                    fontWeight: '400',
                    color: theme.palette.grey[50],
                  }}
                >
                  <FormattedMessage id='wallet.expiry_details.title' />
                </Typography>
                <Stack
                  direction='row'
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    component='span'
                    sx={{
                      fontSize: pxToRem(18),
                      fontWeight: '700',
                      color: theme.palette.grey[80],
                    }}
                  >
                    {userNextExpiredWalletTransaction.restAmount}
                  </Typography>
                  <Typography
                    component='span'
                    sx={{
                      fontSize: pxToRem(16),
                      fontWeight: '400',
                      color: theme.palette.grey[80],
                    }}
                  >
                    &nbsp; <FormattedMessage id='currency.SAR' /> | &nbsp;
                    {DateTime.fromISO(userNextExpiredWalletTransaction.expireAt, {
                      locale,
                    }).toFormat('d MMM')}
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <IconButton
              sx={{
                width: '24px',
                height: '24px',
              }}
            >
              <ArrowForwardIosIcon
                sx={{
                  color: theme.palette.primary.main,
                  transform: isRtl && 'scaleX(-1)',
                }}
              />
            </IconButton>
          </Button>
        )}
        <Box
          onClick={() =>
            setDialog({
              wrapper: true,
              isAboutWalletDialog: true,
            })
          }
          sx={{
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            width: 'fit-content',
          }}
        >
          <InfoOutlinedIcon sx={{ color: theme.palette.primary.main }} />
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: pxToRem(16),
              fontWeight: '700',
              marginInlineStart: '8px',
            }}
          >
            <FormattedMessage id='wallet.learn.more' />
          </Typography>
        </Box>
      </Box>

      <MyTransactions
        accordionSx={accordionSx}
        transactions={transactions}
        isFetching={isFetchingTransactions}
        isErrorTransactions={isErrorTransactions}
        refetchTransactions={() => refetchTransactions()}
      />
    </>
  );
};
